import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    ActivitySquare, ArrowLeft, CheckCircle2, AlertTriangle, XCircle,
    ShieldAlert, PlayCircle, RefreshCcw, Sparkles, Loader2, ScrollText,
} from "lucide-react";

/**
 * /admin/feature-health — Permanent automated QA of feature availability.
 *
 * Displays:
 *   - Dashboard cards (total live, pass/warn/fail/critical, last tested,
 *     last successful full pass, per-workspace coverage %).
 *   - Feature × Workspace matrix (colour-coded verdict per cell).
 *   - Entitlement Consistency report.
 *   - Cross-workspace Security Findings.
 *   - Incident History (institutional memory).
 *   - Pre-Release Gate widget.
 *   - Pre-Demo Gate one-click check.
 */
const VERDICT_STYLE = {
    PASS:     { bg: "bg-emerald-50",  fg: "text-emerald-700",  border: "border-emerald-200",  Icon: CheckCircle2 },
    WARNING:  { bg: "bg-amber-50",    fg: "text-amber-700",    border: "border-amber-200",    Icon: AlertTriangle },
    FAIL:     { bg: "bg-rose-50",     fg: "text-rose-700",     border: "border-rose-200",     Icon: XCircle },
    CRITICAL: { bg: "bg-red-100",     fg: "text-red-800",      border: "border-red-300",      Icon: ShieldAlert },
    OFF:      { bg: "bg-stone-100",   fg: "text-stone-600",    border: "border-stone-200",    Icon: XCircle },
    LIVE:     { bg: "bg-emerald-50",  fg: "text-emerald-700",  border: "border-emerald-200",  Icon: CheckCircle2 },
    BETA:     { bg: "bg-amber-50",    fg: "text-amber-700",    border: "border-amber-200",    Icon: AlertTriangle },
};

function StatusPill({ status, size = "sm", testid }) {
    const s = VERDICT_STYLE[status] || VERDICT_STYLE.OFF;
    const cls = size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs";
    return (
        <span className={`inline-flex items-center gap-1 rounded-full font-semibold ${cls} ${s.bg} ${s.fg} border ${s.border}`} data-testid={testid}>
            <s.Icon size={size === "xs" ? 10 : 12} /> {status}
        </span>
    );
}

export default function FeatureHealth() {
    const [dash, setDash]         = useState(null);
    const [mtx, setMtx]           = useState(null);
    const [cons, setCons]         = useState(null);
    const [sec, setSec]           = useState(null);
    const [inc, setInc]           = useState([]);
    const [gate, setGate]         = useState(null);
    const [demo, setDemo]         = useState(null);
    const [busy, setBusy]         = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const load = async () => {
        try {
            const [d, m, c, s, i, g] = await Promise.all([
                api.get("/feature-health/dashboard"),
                api.get("/feature-health/matrix"),
                api.get("/feature-health/consistency"),
                api.get("/feature-health/security"),
                api.get("/feature-health/incidents?limit=25"),
                api.get("/feature-health/release-gate"),
            ]);
            setDash(d.data); setMtx(m.data); setCons(c.data);
            setSec(s.data); setInc(i.data.incidents || []); setGate(g.data);
        } catch (e) {
            toast.error("Failed to load feature health");
        }
    };
    useEffect(() => { load(); }, []);

    const runFull = async () => {
        setBusy(true);
        try {
            const r = await api.post("/feature-health/run");
            toast.success(`Run complete: ${r.data.verdict}`);
            await load();
        } catch (e) {
            toast.error("Run failed");
        } finally { setBusy(false); }
    };

    const runDemo = async () => {
        setBusy(true);
        try {
            const r = await api.post("/feature-health/demo-readiness");
            setDemo(r.data);
            (r.data.ready ? toast.success : toast.error)(r.data.status);
        } catch { toast.error("Demo check failed"); }
        finally { setBusy(false); }
    };

    const enableAll = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/features/enable-all-production");
            toast.success(`Enabled ${r.data.enabled_live} · Beta ${r.data.kept_beta} · Off ${r.data.kept_off}`);
            await load();
        } catch { toast.error("Enable-all failed"); }
        finally { setBusy(false); setConfirmOpen(false); }
    };

    const workspaces = useMemo(
        () => mtx?.features?.[0] ? Object.keys(mtx.features[0].workspaces) : [],
        [mtx]);

    if (!dash) return <div className="p-8"><Loader2 size={16} className="animate-spin" /></div>;

    const totals = dash.last_totals || {};
    const cov = dash.coverage_percent || {};

    return (
        <div className="max-w-7xl mx-auto p-6 md:p-8" data-testid="feature-health">
            {/* Header */}
            <div className="mb-4 flex items-center gap-2 text-xs">
                <Link to="/super-admin" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Super Admin
                </Link>
            </div>
            <div className="flex items-start justify-between gap-6 mb-6">
                <div className="flex items-start gap-3">
                    <div className="mt-1 rounded-full bg-indigo-50 p-1.5">
                        <ActivitySquare size={16} className="text-indigo-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Feature Health</h1>
                        <p className="text-sm text-stone-500 mt-1 max-w-2xl">
                            Continuous QA for every LIVE feature — cross-checked against the registry,
                            plan entitlements and navigation catalog. No customer presentation should
                            ever be the first time we discover a broken feature.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={runDemo} disabled={busy} data-testid="btn-run-demo">
                        <Sparkles size={12} className="mr-1" /> Run Demo Readiness
                    </Button>
                    <Button size="sm" onClick={runFull} disabled={busy} data-testid="btn-run-full">
                        {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <PlayCircle size={12} className="mr-1" />}
                        Run Full Health Check
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => setConfirmOpen(true)} disabled={busy} data-testid="btn-enable-all">
                        Enable All Production
                    </Button>
                </div>
            </div>

            {/* Release Gate + Demo widget */}
            <div className="grid md:grid-cols-2 gap-3 mb-6">
                <GateCard title="Pre-Release Gate" data={gate} testid="gate-release" />
                <DemoCard demo={demo} testid="gate-demo" />
            </div>

            {/* Dashboard cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="dashboard-cards">
                <MetricCard label="Total Live"     value={dash.total_live}    tone="live" />
                <MetricCard label="Passing"        value={totals.pass || 0}   tone="pass" />
                <MetricCard label="Warnings"       value={totals.warning || 0} tone="warn" />
                <MetricCard label="Failures"       value={totals.fail || 0}   tone="fail" />
                <MetricCard label="Critical"       value={totals.critical || 0} tone="crit" />
                <MetricCard label="Last Tested"    value={dash.last_tested ? new Date(dash.last_tested).toLocaleString() : "—"} tone="neutral" small />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="coverage-cards">
                {[["personal", "Personal"], ["couple_owner", "Couple"],
                  ["family_owner", "Family"], ["business", "Business"],
                  ["showcase", "Showcase"]].map(([k, lbl]) => (
                    <MetricCard key={k} label={`${lbl} Coverage`} value={`${cov[k] || 0}%`} tone="cov" />
                ))}
            </div>

            {/* Feature × Workspace matrix */}
            <section className="mb-8" data-testid="feature-matrix">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold">Feature × Workspace Access Matrix</h2>
                    <Button size="sm" variant="ghost" onClick={load}>
                        <RefreshCcw size={12} className="mr-1" /> Refresh
                    </Button>
                </div>
                <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead className="bg-stone-50 text-stone-500 uppercase tracking-wide text-[10px]">
                            <tr>
                                <th className="text-left px-3 py-2 sticky left-0 bg-stone-50 z-10">Feature</th>
                                <th className="text-left px-3 py-2">Status</th>
                                {workspaces.map((w) => (
                                    <th key={w} className="px-2 py-2 text-center whitespace-nowrap">
                                        {mtx.features[0].workspaces[w].label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {mtx.features.map((f) => (
                                <tr key={f.feature}>
                                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                                        <div className="font-medium">{f.label}</div>
                                        <div className="text-[10px] text-stone-400">{f.feature}</div>
                                    </td>
                                    <td className="px-3 py-2"><StatusPill status={f.product_status} size="xs" /></td>
                                    {workspaces.map((w) => {
                                        const cell = f.workspaces[w];
                                        return (
                                            <td key={w} className="px-2 py-2 text-center" title={cell.detail}>
                                                <StatusPill status={cell.verdict} size="xs" testid={`cell-${f.feature}-${w}`} />
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Consistency & Security */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
                <section data-testid="consistency">
                    <h2 className="text-lg font-bold mb-3">Entitlement Consistency</h2>
                    <div className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100">
                        {(!cons?.mismatches || cons.mismatches.length === 0) && (
                            <div className="p-4 text-sm text-emerald-700 flex items-center gap-2">
                                <CheckCircle2 size={14} /> All entitlements consistent.
                            </div>
                        )}
                        {cons?.mismatches?.map((m, i) => (
                            <div key={i} className="p-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <StatusPill status={m.result.startsWith("FAIL") ? "FAIL" : "WARNING"} size="xs" />
                                    <span className="font-medium">{m.feature}</span>
                                </div>
                                <div className="text-xs text-stone-500 mt-1">{m.detail}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <section data-testid="security">
                    <h2 className="text-lg font-bold mb-3">Cross-Workspace Security</h2>
                    <div className="rounded-xl border border-stone-200 bg-white p-3">
                        {(!sec?.findings || sec.findings.length === 0) ? (
                            <div className="text-sm text-emerald-700 flex items-center gap-2">
                                <CheckCircle2 size={14} /> No cross-workspace leaks detected across {sec?.checks?.length || 0} probes.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {sec.findings.map((f, i) => (
                                    <div key={i} className="flex items-start gap-2 text-sm">
                                        <StatusPill status={f.severity} size="xs" />
                                        <div>
                                            <div className="font-medium">{f.collection}</div>
                                            <div className="text-xs text-stone-500">{f.detail}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* Incident history */}
            <section data-testid="incidents">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <ScrollText size={14} /> Incident History
                </h2>
                <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead className="bg-stone-50 text-stone-500 uppercase tracking-wide text-[10px]">
                            <tr>
                                <th className="text-left px-3 py-2">Feature</th>
                                <th className="text-left px-3 py-2">Workspace</th>
                                <th className="text-left px-3 py-2">Severity</th>
                                <th className="text-left px-3 py-2">Expected</th>
                                <th className="text-left px-3 py-2">Actual</th>
                                <th className="text-left px-3 py-2">Root Cause</th>
                                <th className="text-left px-3 py-2">Resolved</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {inc.length === 0 && (
                                <tr><td colSpan={7} className="p-4 text-center text-stone-400">No incidents recorded.</td></tr>
                            )}
                            {inc.map((r) => (
                                <tr key={r.id}>
                                    <td className="px-3 py-2 font-medium">{r.feature || "—"}</td>
                                    <td className="px-3 py-2">{r.workspace_type || "—"}</td>
                                    <td className="px-3 py-2"><StatusPill status={r.severity || "WARNING"} size="xs" /></td>
                                    <td className="px-3 py-2 text-stone-500">{r.expected || "—"}</td>
                                    <td className="px-3 py-2">{r.actual || "—"}</td>
                                    <td className="px-3 py-2 text-stone-500">{r.root_cause || "—"}</td>
                                    <td className="px-3 py-2 text-stone-500">{r.resolved_at ? new Date(r.resolved_at).toLocaleDateString() : <span className="text-amber-600">Open</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Enable-All confirm dialog */}
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent data-testid="enable-all-dialog">
                    <DialogHeader>
                        <DialogTitle>Enable All Production Features?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-stone-600 space-y-2">
                        <p>This flips every production-ready feature flag to <b>LIVE / everyone</b>.</p>
                        <p className="text-xs text-stone-500">
                            Sensitive features (VAULT Connect, Trusted / Emergency Access) stay OFF.
                            VAULT Intelligence and Documents AI stay BETA.
                        </p>
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                            Note: You are executing this against the <b>current environment</b>. For production,
                            run it after Save & Deploy from Preview → Production.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={enableAll} disabled={busy} data-testid="confirm-enable-all">
                            {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
                            Enable all now
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function MetricCard({ label, value, tone = "neutral", small }) {
    const toneCls = {
        live:    "bg-emerald-50 text-emerald-700 border-emerald-200",
        pass:    "bg-emerald-50 text-emerald-700 border-emerald-200",
        warn:    "bg-amber-50 text-amber-700 border-amber-200",
        fail:    "bg-rose-50 text-rose-700 border-rose-200",
        crit:    "bg-red-100 text-red-800 border-red-300",
        cov:     "bg-indigo-50 text-indigo-700 border-indigo-200",
        neutral: "bg-white text-stone-800 border-stone-200",
    }[tone];
    return (
        <div className={`rounded-xl border p-3 ${toneCls}`}>
            <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
            <div className={small ? "text-xs mt-1" : "text-2xl font-bold mt-1"}>{value}</div>
        </div>
    );
}

function GateCard({ title, data, testid }) {
    if (!data) return null;
    const status = data.allowed ? "PASS" : "FAIL";
    const s = VERDICT_STYLE[status];
    return (
        <div className={`rounded-xl border p-4 ${s.border} ${s.bg}`} data-testid={testid}>
            <div className="flex items-center gap-2">
                <s.Icon size={14} className={s.fg} />
                <span className={`text-xs font-semibold uppercase ${s.fg}`}>{title}</span>
            </div>
            <div className="text-sm mt-2 font-medium">{data.allowed ? "Production release allowed" : "Production release BLOCKED"}</div>
            <div className="text-xs text-stone-600 mt-1">{data.reason}</div>
            {data.at && <div className="text-[10px] text-stone-400 mt-1">Last run: {new Date(data.at).toLocaleString()}</div>}
        </div>
    );
}

function DemoCard({ demo, testid }) {
    if (!demo) return (
        <div className="rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-500" data-testid={testid}>
            <div className="text-xs font-semibold uppercase text-stone-500 mb-2">Pre-Demo Gate</div>
            Click <b>Run Demo Readiness</b> to test the VAULT Showcase.
        </div>
    );
    const status = demo.ready ? "PASS" : "FAIL";
    const s = VERDICT_STYLE[status];
    return (
        <div className={`rounded-xl border p-4 ${s.border} ${s.bg}`} data-testid={testid}>
            <div className="flex items-center gap-2">
                <s.Icon size={14} className={s.fg} />
                <span className={`text-xs font-semibold uppercase ${s.fg}`}>Pre-Demo Gate</span>
            </div>
            <div className="text-sm mt-2 font-medium">{demo.status}</div>
            {demo.failures?.length > 0 && (
                <ul className="text-xs text-stone-700 mt-2 list-disc pl-5 space-y-0.5">
                    {demo.failures.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
            )}
            <div className="text-[10px] text-stone-400 mt-2">Checked: {new Date(demo.checked_at).toLocaleString()}</div>
        </div>
    );
}
